import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import App from "./App.tsx";
import "./index.css";
import { initNativeShell } from "./utils/nativeBootstrap";
import { initAppVersion, appVersionFull } from "./lib/env";
// Side-effect import: the CarPlay bridge's connect listener + Stage 0
// heartbeat poll must run from APP LAUNCH, not from the first time
// navigation opens. (NavigationView lazy-loads its module — Morgan's
// parked driveway test showed "JS ♥ —" because the bridge file had
// literally never executed.)
import "./native/carplayBridge";
import { setupServiceWorker } from "./registerSW";
import { applyWaitingUpdate } from "./utils/applyWaitingUpdate";
import { markOfflineShellReady } from "./components/OfflineShellNotice";
import { UpdateReadyNotice, setUpdateHandler, markUpdateReady } from "./components/UpdateReadyNotice";
import { initSentry, SentryErrorBoundary } from "./utils/sentry";
import { sweepPoiCache } from "./utils/poiCache";
import { sweepAllBudgets } from "./utils/storageBudget";
import { ErrorFallback } from "./components/ErrorFallback";
// Privacy-friendly usage analytics. Only beacons when deployed on Vercel
// (production web) — a no-op locally and in the native build, so it's safe
// to render unconditionally.
import { Analytics } from "@vercel/analytics/react";
// Diagnostics gate — see src/lib/debugFlag.ts. Must run before ANY module
// logs, so it is installed first thing below.
import { installDiagnosticsGate } from "./lib/debugFlag";
import { recordFirstOpen } from "./utils/firstOpen";

// Chunk-load recovery. When a new build deploys, Vite renames the hashed
// code-split chunks. A tab still running the OLD bundle 404s the moment it
// lazy-loads a panel ("Importing a module script failed" / "Failed to fetch
// dynamically imported module") — e.g. opening "Why this route?". Instead of
// dumping the user on the error screen, quietly reload once to pull the fresh
// bundle. A short cooldown prevents reload loops if it's a genuine failure.
if (typeof window !== "undefined") {
  const CHUNK_ERR_RE =
    /importing a module script failed|failed to fetch dynamically imported module|error loading dynamically imported module|module script failed/i;
  const reloadOnceForChunk = () => {
    try {
      const last = Number(sessionStorage.getItem("pleasant.chunkReloadAt") || 0);
      if (Date.now() - last < 10_000) return; // already tried just now — don't loop
      sessionStorage.setItem("pleasant.chunkReloadAt", String(Date.now()));
    } catch { /* ignore */ }
    window.location.reload();
  };
  // Vite's own signal for a failed dynamic-import preload. Do NOT call
  // e.preventDefault() here: Vite's preload helper treats a cancelled event
  // as "handled" and RESOLVES the dynamic import() with `undefined` instead
  // of rejecting it, so React.lazy then throws
  // "undefined is not an object (evaluating 'e._result.default')" into the
  // error boundary before (or instead of) the reload — Sentry issue
  // 7705655098, /overlooked on Safari. Leaving the event uncancelled lets the
  // import reject normally, which lazyWithReload in App.tsx handles.
  window.addEventListener("vite:preloadError", () => {
    reloadOnceForChunk();
  });
  // Catch-all for the runtime error + the rejected dynamic import() promise.
  window.addEventListener("error", (e) => {
    if (CHUNK_ERR_RE.test(String(e?.message ?? ""))) reloadOnceForChunk();
  });
  window.addEventListener("unhandledrejection", (e) => {
    const msg = (e?.reason && (e.reason.message ?? String(e.reason))) || "";
    if (CHUNK_ERR_RE.test(String(msg))) reloadOnceForChunk();
  });
}

// DIAGNOSTIC OUTPUT IS OPT-IN (Morgan, Aug 27 2026). The routing pipeline
// printed itself to the console on every production search — pool contents,
// provider names, scores, thresholds, which gate rejected what. Off by
// default now; `?debug=1` (or `?shadow=1`) restores every line, exactly as
// before, and dev builds are unchanged. Installed here, above the build
// banner, so no module can log before the gate is in place.
installDiagnosticsGate();

// UNBRICK THE SIGN-IN. The POI cache mints a localStorage key per ~110m cell
// and, before this sweep existed, never dropped them -- roughly a week of
// driving filled the quota. Once full, Supabase cannot write its auth token
// (the sign-in page fails with "exceeded the quota") and saved trips stop
// persisting. Sweeping here, before anything touches storage, is what gets
// an already-full device working again.
try {
  const dropped = sweepPoiCache();
  if (dropped) console.info(`[poiCache] swept ${dropped} stale cells at startup`);
} catch { /* never block boot on a cache sweep */ }
// Same disease, second organ (Sep 16 2026): the Overpass cell cache and the
// Google Directions cache mint a key per place too. One sweeper, one budget
// list -- add any new per-place cache to STARTUP_BUDGETS, not here.
try {
  const dropped = sweepAllBudgets();
  if (dropped) console.info(`[storageBudget] swept ${dropped} stale cache keys at startup`);
} catch { /* never block boot on a cache sweep */ }

// FIRST LINE OF EVERY CONSOLE: which build this is. Three debugging
// round-trips on Aug 2 were spent discovering the tab was running old code.
// Now it's never a question — compare this sha against `git log --oneline -1`.
declare const __BUILD_SHA__: string;
declare const __BUILD_TIME__: string;
// The version joins the sha here because "which build am I testing" and
// "which version is this" were separate questions with separate wrong
// answers. At this point it is the BUILD-TIME stamp; initAppVersion below
// corrects it from the OS and warns if the two disagree.
console.log(`PLEASANT BUILD ${__BUILD_SHA__} v${appVersionFull()} (${__BUILD_TIME__})`);

// Crash reporting. No-ops cleanly when VITE_SENTRY_DSN isn't set, so the app
// keeps working in any environment that hasn't been configured. Initialized
// before anything else so init-path errors land in Sentry too.
initSentry();

// After a redeploy the previously-loaded HTML can reference hashed chunk
// filenames that no longer exist on the server. Dynamic import then throws
// "Importing a module script failed" and the user sees a blank screen.
// Catch that at the window level and force one fresh reload (guarded by
// sessionStorage so we never loop if the failure is real).
if (typeof window !== "undefined") {
  // Nuke any service worker + caches that may be serving a stale shell, then
  // hard-reload. Used both as the chunk-failure recovery path and as a manual
  // escape hatch (window.__pleasantHardReset()).
  const hardResetAndReload = async () => {
    try {
      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister().catch(() => undefined)));
      }
      if (typeof caches !== "undefined") {
        const names = await caches.keys();
        await Promise.all(names.map((n) => caches.delete(n).catch(() => undefined)));
      }
    } catch {
      /* best-effort */
    }
    // Bust any HTTP caches with a query param so we definitely get fresh HTML.
    const url = new URL(window.location.href);
    url.searchParams.set("_swreset", Date.now().toString());
    window.location.replace(url.toString());
  };
  (window as unknown as { __pleasantHardReset?: () => void }).__pleasantHardReset =
    () => { void hardResetAndReload(); };

  const handleChunkFailure = (msg: string | undefined) => {
    if (!msg) return;
    if (
      msg.includes("Importing a module script failed") ||
      msg.includes("Failed to fetch dynamically imported module") ||
      msg.includes("error loading dynamically imported module") ||
      msg.includes("Unable to preload CSS")
    ) {
      const flag = "chunk-reload:global";
      if (!sessionStorage.getItem(flag)) {
        sessionStorage.setItem(flag, "1");
        // Clear SW + caches first — a plain reload often re-serves the same
        // stale shell that caused the chunk error.
        void hardResetAndReload();
      }
    }
  };
  window.addEventListener("error", (e) => handleChunkFailure(e.message));
  window.addEventListener("unhandledrejection", (e) => {
    const reason: unknown = e.reason;
    const msg =
      typeof reason === "string"
        ? reason
        : reason instanceof Error
        ? reason.message
        : undefined;
    handleChunkFailure(msg);
  });
}

// IN-HOUSE PAGE-VIEW BEACON (Morgan, Aug 3: web traffic on /admin). One
// anonymous insert per tab-session per path — a random session string and a
// path, nothing else; no IP, no user id, nothing identifying. Vercel
// Analytics keeps running in parallel; this exists so the numbers appear on
// OUR dashboard. Fire-and-forget: any failure is swallowed, the app never
// waits on it.
if (typeof window !== "undefined") {
  void (async () => {
    try {
      const KEY = "pleasant.viewSession";
      let sid = sessionStorage.getItem(KEY);
      if (!sid) {
        sid = Math.random().toString(36).slice(2) + Date.now().toString(36);
        sessionStorage.setItem(KEY, sid);
      }
      const path = window.location.pathname || "/";
      const seenKey = `pleasant.viewed:${path}`;
      if (sessionStorage.getItem(seenKey)) return; // one per path per session
      sessionStorage.setItem(seenKey, "1");
      const { supabase } = await import("@/integrations/supabase/client");
      void supabase.from("page_views").insert({ session_id: sid, path }).then(
        () => undefined,
        () => undefined,
      );
    } catch {
      /* never let analytics touch the app */
    }
  })();
}

// Configure native status bar + hide the Capacitor splash once React mounts.
// No-op on web.
// Ask the OS what version it actually installed, and correct the build-time
// stamp if they disagree. Everything written before this resolves carries the
// build stamp, which is right in every case except a stale bundle — and that
// case now says so in the console instead of silently mislabelling a release.
void initAppVersion();
// PLE-13: stamp first-open BEFORE onboarding writes a profile, so NoticeBanner
// can tell a brand-new install from an existing one. Never throws.
recordFirstOpen();
// Guarded (Sep 2026, Safari boot-stuck investigation): this is a
// synchronous, unawaited call sitting before createRoot() and the boot-
// screen removal below. initNativeShell() itself early-returns off-native,
// but nothing upstream protected the app from a throw here reaching the top
// of the module and aborting everything after it, including the removal of
// the #pleasant-boot screen (see index.html's safety-net script, added at
// the same time, for the guarantee that covers throws we can't guard here).
try {
  initNativeShell();
} catch (err) {
  console.warn("[boot] initNativeShell failed:", err);
}

// Register the offline-shell service worker. No-ops in the Lovable preview
// iframe and on preview hosts (see registerSW.ts for the guards).
//
// RELIABLE DEPLOYS (Morgan): the recurring "I'm seeing old code / it's still
// slow" came from the service worker never handing over the new build — the
// old worker kept serving the stale bundle because activation waited on a
// manual toast tap that often never happened. Fix: (1) poll for new builds so
// long-open tabs notice deploys, and (2) auto-apply a ready update the moment
// the tab is backgrounded — the reload happens while you're looking away, so
// you return to fresh code with no mid-session yank. The toast stays for
// instant manual apply.
const updatePending = { ready: false, applied: false };
// Guarded (Sep 2026, Safari boot-stuck investigation): setupServiceWorker()
// already catches a throw from its own registerSW(...) call internally, but
// this call site — and everything it wires up below — sat unguarded between
// two calls we've now confirmed matter (createRoot and the boot-screen
// removal). Belt-and-suspenders so nothing in this block can take the rest
// of the module down with it.
let updateSW: ((reload?: boolean) => Promise<void>) | null = null;
// Kept for the toast's Reload: a first-visit page needs the waiting worker
// itself (see applyWaitingUpdate in registerSW.ts).
let swRegistration: ServiceWorkerRegistration | null = null;
try {
  updateSW = setupServiceWorker({
    onOfflineReady: () => {
      markOfflineShellReady();
      window.dispatchEvent(new Event("pleasant:offline-ready"));
    },
    onNeedRefresh: () => {
      markUpdateReady();
      updatePending.ready = true;
    },
    onRegistered: (reg) => {
      if (!reg) return;
      swRegistration = reg;
      // Poll for a new deploy every 60s so always-open tabs pick it up.
      setInterval(() => { void reg.update().catch(() => undefined); }, 60_000);
    },
  });
} catch (err) {
  console.warn("[boot] setupServiceWorker failed:", err);
}
// Hand the real updater to the toast: calling it activates the waiting worker
// and reloads into the new build — including on a first visit, where the
// plugin's own reload never fires (PLE-13).
if (updateSW) {
  setUpdateHandler(() => {
    void applyWaitingUpdate({ registration: swRegistration, updateSW: updateSW! });
  });
  // Apply a pending update when the user looks away (tab hidden) — never while
  // a turn-by-turn drive is live (window.__pleasantInNavigation), so we don't
  // yank an active navigation.
  document.addEventListener("visibilitychange", () => {
    if (
      document.visibilityState === "hidden" &&
      updatePending.ready &&
      !updatePending.applied &&
      !(window as { __pleasantInNavigation?: boolean }).__pleasantInNavigation
    ) {
      updatePending.applied = true;
      void updateSW!(true);
    }
  });
}

createRoot(document.getElementById("root")!).render(
  <HelmetProvider>
    {/* SentryErrorBoundary catches any uncaught render error in the tree
     *  and shows a calm fallback UI. When VITE_SENTRY_DSN is unset Sentry's
     *  ErrorBoundary still works locally — it just doesn't ship the report. */}
    <SentryErrorBoundary
      fallback={({ error, resetError }) => (
        <ErrorFallback error={error} resetError={resetError} />
      )}
    >
      <App />
      <UpdateReadyNotice />
      <Analytics />
    </SentryErrorBoundary>
  </HelmetProvider>
);

// Remove the boot loader injected by index.html as soon as React is rendered,
// so the web/PWA cold-start fades cleanly into the app instead of flashing
// a blank background.
if (typeof window !== "undefined") {
  requestAnimationFrame(() => requestAnimationFrame(() => {
    const boot = document.getElementById("pleasant-boot");
    if (boot) {
      boot.style.opacity = "0";
      window.setTimeout(() => boot.remove(), 320);
    }
  }));
}
